import { catchError, Observable, of } from 'rxjs';
import { Country } from '@app/shared/models/country';
import { COUNTRIES_SE } from '@api/countries/se-countries';
import { COUNTRIES_EN } from '@api/countries/en-countries';
import { CurrentUser } from '@core/auth/current-user';

export const getCustomerIds = (currentUser: CurrentUser, filterCustomerIds?: number[]): number[] | undefined => {
  const customerId = currentUser?.currentCustomerId || currentUser?.customerId;
  return customerId ? [customerId] : filterCustomerIds;
};

// Helper function to handle API call with error catching
// Should be used in forkJoin to handle separate API calls errors and not break the whole operation
export const apiCallCatchError = <T>(apiCall: Observable<T>): Observable<T | null> => {
  return apiCall.pipe(
    catchError(error => {
      console.error('API call failed:', error);
      return of(null);
    })
  );
};

export const sumObjects = (...objects) => {
  return objects.reduce((acc, obj) => {
    if (!obj || typeof obj !== 'object') return acc;

    for (const key in obj) {
      if (typeof obj[key] === 'number') {
        acc[key] = (acc[key] || 0) + obj[key];
      }
    }
    return acc;
  }, {});
};

export const objectHasAnyPositiveValue = obj => {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some(value => typeof value === 'number' && value > 0);
};

// Function to convert seconds to a string like '2days 14h 22s'
export const calcTimeFromInteger = (totalSeconds: number): string => {
  if (isNaN(totalSeconds) || totalSeconds < 0) return '-';
  if (totalSeconds === 0) return '0s';

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts = [];

  if (days > 0) parts.push(`${days}days`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

  return parts.join(' ');
};

// Function to build a map from an array of countries
export const buildCountryMap = (currentLang): Record<string, string> => {
  const countries = currentLang === 'se' ? COUNTRIES_SE : COUNTRIES_EN;

  return countries.reduce((acc, country) => {
    acc[country.alpha2Code] = country.name;
    return acc;
  }, {} as Record<string, string>);
};

export const downloadFile = (data: BlobPart, fileName: string, mimeType: string): void => {
  const blob = new Blob([data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};


export const base64ToBlob = (base64: string): Blob => {
  const [x, data] = base64.split(',');
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: 'image/jpg' });
}